import { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripePaymentElementChangeEvent } from "@stripe/stripe-js";

import { SubmitStripeFormParams } from "@covertree/features";
import {
  PurchaseAffiliation,
  PurchaseCheckoutOption,
  useTracking,
} from "@covertree/tracking";
import { Form } from "@covertree/ui";
import {
  PaymentPlan,
  Policyholder,
  useInitiatePolicyPurchaseMutation,
  useRemovePaymentQuoteMutation,
} from "@covertree/new-business-graphql";
import { Toast } from "@covertree/utils";

import { ConfirmPurchase } from "../ConfirmPurchase";
import { useCheckout } from "../../CheckoutProvider";
import {
  AdditionalInterestContainer,
  mapAdditionalInterestToPayload,
} from "../../AdditionalInterest";

import { PaymentPlanKey } from "../../types";
import {
  getTotalCostFromPaymentPlan,
  mapPaymentPlanKeyToPaymentSchedule,
  mapStripePaymentMethodToPaymentMethod,
} from "../../utils";

import { createStripeFormValidationSchema, defaultValues } from "./form";

interface Props {
  policyholder: Policyholder;
  selectedPaymentPlan: PaymentPlan;
  selectedPaymentPlanKey: PaymentPlanKey;
}

export function StripePayment({
  policyholder,
  selectedPaymentPlan,
  selectedPaymentPlanKey,
}: Props) {
  const elements = useElements();
  const stripe = useStripe();

  const [stripeElementState, setStripeElementState] =
    useState<StripePaymentElementChangeEvent>();

  const {
    anythingLoading,
    forceAgentSignResponsibility,
    isD2C,
    policyLocator,
    onError,
    setPaymentLoading,
    onPaymentSuccess,
    additionalInterests,
    isAdditionalInterestsEnabled,
  } = useCheckout();

  const { trackPurchase } = useTracking();

  const form = useForm({
    defaultValues: {
      ...defaultValues,
      ...(forceAgentSignResponsibility
        ? { agentSignResponsibility: true }
        : {}),
    },
    mode: "all",
    resolver: yupResolver(
      createStripeFormValidationSchema(!!isD2C, forceAgentSignResponsibility)
    ),
  });

  const {
    formState: { isValid: isFormValid },
    getValues,
    handleSubmit: submit,
  } = form;

  const [initiatePolicyPurchase] = useInitiatePolicyPurchaseMutation();
  const [removePaymentQuote] = useRemovePaymentQuoteMutation();

  const onStripeSubmit = useCallback(
    async ({ elements, stripe, paymentMethod }: SubmitStripeFormParams) => {
      const signResponsibilityChecked = isD2C
        ? undefined
        : !!getValues("agentSignResponsibility");

      const defaultPayload = {
        policyLocator,
        paymentSchedule: mapPaymentPlanKeyToPaymentSchedule(
          selectedPaymentPlanKey
        ),
        paymentMethod: mapStripePaymentMethodToPaymentMethod(paymentMethod),
        signResponsibilityChecked,
      };

      const stripePaymentPayload = isAdditionalInterestsEnabled
        ? {
            ...defaultPayload,
            additionalInterests:
              mapAdditionalInterestToPayload(additionalInterests),
          }
        : defaultPayload;

      try {
        setPaymentLoading(true);
        const { data } = await initiatePolicyPurchase({
          variables: stripePaymentPayload,
        });

        if (!data?.initiatePolicyPurchase.clientSecret) {
          throw new Error("Failed to initiate policy purchase");
        }

        const { error: paymentError, paymentIntent } =
          await stripe.confirmPayment({
            clientSecret: data.initiatePolicyPurchase.clientSecret,
            elements: elements!,
            redirect: "if_required",
            confirmParams: {
              payment_method_data: {
                billing_details: {
                  name: `${policyholder.firstName} ${policyholder.lastName}`,
                  email: policyholder.emailAddress,
                  address: {
                    country: "US",
                    postal_code: policyholder.mailingAddress.zipCode,
                    state: policyholder.mailingAddress.state,
                    city: policyholder.mailingAddress.city,
                    line1: `${policyholder.mailingAddress.streetAddress}${!!policyholder.mailingAddress.unitNumber ? ` #${policyholder.mailingAddress.unitNumber}` : ""}`,
                  },
                },
              },
              return_url: "https://covertree.com",
            },
          });

        if (
          paymentError ||
          (paymentMethod === "card" && paymentIntent?.status !== "succeeded") ||
          (paymentMethod === "us_bank_account" &&
            !["processing", "succeeded"].includes(paymentIntent?.status))
        ) {
          try {
            await removePaymentQuote({
              variables: {
                policyLocator,
              },
            });
          } catch (error) {
            // We exit the screen to force user to reload checkout screen and therefore initiatePolicyPurchase once again
            // It tries to clear quotes on the beginning of execution
            setPaymentLoading(false);
            onError(error as Error);
            return;
          }

          setPaymentLoading(false);
          const message =
            paymentError?.message ?? "Something went wrong, please try again.";
          Toast.showError(message);

          return;
        }

        trackPurchase({
          affiliation: isD2C
            ? PurchaseAffiliation.D2C
            : PurchaseAffiliation.Agent,
          checkout_option: PurchaseCheckoutOption.CreditDebitCard,
          transaction_id: policyLocator,
          value: getTotalCostFromPaymentPlan(selectedPaymentPlan),
        });

        setPaymentLoading(false);
        onPaymentSuccess(signResponsibilityChecked);
      } catch (error) {
        try {
          await removePaymentQuote({
            variables: {
              policyLocator,
            },
          });
        } catch {}
        setPaymentLoading(false);
        onError(error as Error);
      }
    },
    [
      initiatePolicyPurchase,
      isD2C,
      getValues,
      onError,
      onPaymentSuccess,
      policyLocator,
      removePaymentQuote,
      selectedPaymentPlan,
      selectedPaymentPlanKey,
      setPaymentLoading,
      trackPurchase,
      additionalInterests,
    ]
  );

  // Hook form handler which will take care of validating consents
  const onFormSubmit = useCallback(async () => {
    if (!stripe || !elements || !stripeElementState || anythingLoading) {
      return;
    }

    const { error, selectedPaymentMethod } = await elements.submit();

    if (error) {
      onError(new Error(error.message));
      return;
    }

    if (!stripeElementState.complete) {
      return;
    }

    await onStripeSubmit({
      elements,
      stripe,
      paymentMethod: selectedPaymentMethod!,
    });
  }, [
    anythingLoading,
    onError,
    onStripeSubmit,
    stripe,
    elements,
    stripeElementState,
  ]);

  return (
    <FormProvider {...form}>
      <Form onSubmit={submit(onFormSubmit)}>
        <PaymentElement
          options={{
            layout: "tabs",
            business: {
              name: "CoverTree",
            },
            readOnly: anythingLoading,
            fields: {
              billingDetails: {
                address: "never",
                email: "never",
                name: "never",
              },
            },
            terms: {
              card: "never",
              usBankAccount: "never",
            },
            paymentMethodOrder: ["us_bank_account", "card"],
          }}
          onChange={(e) => setStripeElementState(e)}
        />
        {isAdditionalInterestsEnabled && <AdditionalInterestContainer />}
        <ConfirmPurchase
          disabled={
            anythingLoading || !stripeElementState?.complete || !isFormValid
          }
        />
      </Form>
    </FormProvider>
  );
}
